import {type Address} from '../cluster/Address.ts';
import {type Properties, type PropertyDefinition} from '../config/Properties.ts';
import {type DiscoveryConfig, type DiscoveryStrategyConfig} from '../config/DiscoveryConfig.ts';
import {type Logger} from '../logging.ts';
import {KubernetesDiscoveryStrategyFactory} from '../kubernetes/KubernetesDiscoveryStrategyFactory.ts';
import {MulticastDiscoveryStrategyFactory} from './multicast/MulticastDiscoveryStrategyFactory.ts';

export interface DiscoveryServiceFactory {
    newDiscoveryService(settings: DiscoveryServiceSettings): DiscoveryService
}

export interface DiscoveryService {
    start(): Promise<void>
    discover(): Promise<Iterable<DiscoveryNode>>
    close(): Promise<void>
}


export type DiscoveryServiceSettings = {
    readonly node: Promise<DiscoveryNode>
    readonly mode: DiscoveryMode
    readonly conf: DiscoveryConfig
    readonly auto: boolean
    readonly logger: Logger
}

export type DiscoveryMode = 'member' | 'client';

export class DefaultDiscoveryServiceFactory implements DiscoveryServiceFactory {
    newDiscoveryService(settings: DiscoveryServiceSettings): DiscoveryService {
        return new DefaultDiscoveryService(settings);
    }
}

function verifyNoUnknownProperties(mappedProperties: Properties, allProperties: Properties) {
    const unknownProperties = Object.keys(allProperties).filter(key => !mappedProperties.hasOwnProperty(key));
    if (unknownProperties.length > 0) {
        throw new Error(`Unknown properties: ${unknownProperties.join(', ')} on discovery strategy`);
    }
}

function prepareProperties(properties: Properties,
                           propertyDefinitions: readonly PropertyDefinition[]) {
    const mappedProperties: Properties = {};
    for (const propertyDefinition of propertyDefinitions) {
        const propertyKey = propertyDefinition.key;
        if (properties[propertyKey] == undefined) {
            if (!propertyDefinition.optional) {
                throw new Error(`Missing required property: ${propertyKey} on discovery strategy`);
            }
            continue;
        }
        const value = properties[propertyKey];
        const converter = propertyDefinition.converter;
        const mappedValue = converter.convert(value);

        mappedProperties[propertyKey] = mappedValue;
    }

    verifyNoUnknownProperties(mappedProperties, properties);
    return mappedProperties;

}

export class DefaultDiscoveryService implements DiscoveryService {
    private readonly settings: DiscoveryServiceSettings;
    private readonly strategies: Array<DiscoveryStrategy> = [];
    constructor(settings: DiscoveryServiceSettings) {
        this.settings = settings;
    }

    async start(): Promise<void> {
        this.strategies.push(...await this.loadDiscoveryStrategies());
        for (const strategy of this.strategies) {
            await strategy.start();
        }
    }

    async discover(): Promise<Iterable<DiscoveryNode>> {
        const discoveryNodes = new Set<DiscoveryNode>();
        for (const strategy of this.strategies) {
            const candidates = await strategy.discover();
            if (candidates) {
                for (const candidate of candidates) {
                    if (this.validateCandidate(candidate)) {
                        discoveryNodes.add(candidate);
                    }
                }
            }

        }

        return discoveryNodes;
    }

    async close(): Promise<void> {
        for (const strategy of this.strategies) {
            await strategy.close();
        }
    }

    private validateCandidate(candidate: DiscoveryNode) : boolean {
        // todo
        return true;
    }

    private async loadDiscoveryStrategies(): Promise<Iterable<DiscoveryStrategy>> {

        const discoveryStrategyConfigs = this.settings.conf.discoveryStrategyConfigs;
        const factories = this.collectFactories(discoveryStrategyConfigs);

        const discoveryStrategies = new Array<DiscoveryStrategy>();
        for (const config of discoveryStrategyConfigs) {
            const discoveryStrategy = await this.buildDiscoveryStrategy(config, factories);
            discoveryStrategies.push(discoveryStrategy);
        }
        const logger = this.settings.logger;
        if (discoveryStrategies.length == 0 && this.settings.auto) {
            logger.debug(`Discovery auto-detection enabled, trying to detect discovery strategy`);
            const autoDetectedFactory = await this.detectDiscoveryStrategyFactory(factories);
            if (autoDetectedFactory) {
                logger.debug(`Auto-detected discovery strategy: ${autoDetectedFactory.constructor.name}`);
                discoveryStrategies.push(autoDetectedFactory.newDiscoveryStrategy(await this.settings.node, logger, {}));
            }
            else {
                logger.debug(`No discovery strategy auto-detected`);
            }
        }
        return discoveryStrategies;
    }

    private collectFactories(strategyConfigs: ReadonlyArray<DiscoveryStrategyConfig>): DiscoveryStrategyFactory[] {

        const knownFactories = [new MulticastDiscoveryStrategyFactory(), new KubernetesDiscoveryStrategyFactory()]

        const factories = new Array<DiscoveryStrategyFactory>();

        for (const factory of knownFactories) {
            factories.push(factory);
        }

        for (const config of strategyConfigs) {
            const factory = config.discoveryStrategyFactory;
            if (factory) {
                factories.push(factory);
            }
        }

        return factories;
    }

    private async buildDiscoveryStrategy(config: DiscoveryStrategyConfig,
                                         factories: DiscoveryStrategyFactory[]): Promise<DiscoveryStrategy> {
        const discoveryNode = await this.settings.node;
        const logger = this.settings.logger;

        for (const factory of factories) {
            if (factory.constructor.name == config.discoveryStrategyFactory.constructor.name) {
                const properties = prepareProperties(config.properties, factory.getConfigurationProperties());
                return factory.newDiscoveryStrategy(discoveryNode, logger, properties);
            }
        }
        throw new Error('DiscoveryStrategyFactory not found');
    }

    private async detectDiscoveryStrategyFactory(factories: DiscoveryStrategyFactory[]) {
        let highestPriorityFactory: DiscoveryStrategyFactory;
        for (const factory of factories) {
            try {
                if (await factory.isAutoDetectionApplicable()) {
                    if (highestPriorityFactory === undefined || factory.priority > highestPriorityFactory.priority) {
                        highestPriorityFactory = factory;
                    }
                }
            } catch (error) {
                // todo log error
            }
        }
        return highestPriorityFactory;
    }


}

export interface DiscoveryStrategy {
    start(): Promise<void>
    discover(): Promise<Iterable<DiscoveryNode> | undefined>
    close(): Promise<void>
}

export interface DiscoveryStrategyFactory {
    readonly priority: number
    isAutoDetectionApplicable(): Promise<boolean>
    newDiscoveryStrategy(node: DiscoveryNode, logger: Logger, settings: Properties): DiscoveryStrategy
    getConfigurationProperties(): ReadonlyArray<PropertyDefinition>
}
export const DiscoveryStrategyPriorities = {
    UNKNOWN: 0,
    PLATFORM: 20,
    CUSTOM: 50,
}

export interface DiscoveryNode {
    readonly publicAddress: Address;
    readonly privateAddress: Address;
    readonly properties: ReadonlyMap<string, unknown>;
}
